import { BUCKETS, type Bucket } from '@portal-contract';
import { useI18n } from '@/i18n/I18nProvider';
import { formatAmount } from '@/lib/format';
import type { MessageKey } from '@/i18n/translate';

const BAR_COLOR: Record<Bucket, string> = {
  current: 'bg-green-400',
  '1-30': 'bg-yellow-400',
  '31-60': 'bg-orange-400',
  '61-90': 'bg-red-300',
  '90+': 'bg-red-500',
};

/**
 * Open balance by age (plan 5.3): a proportional bar for a quick read plus a real table with the
 * numbers, so nothing depends on colour. The bar is decoration (aria-hidden); the table is the content.
 */
export function AgingTable({ aging, currency, id }: { aging: Record<Bucket, number>; currency: string; id: string }) {
  const { t, lang } = useI18n();
  // Display-only: the sum drives bar widths, it is never shown.
  const total = BUCKETS.reduce((s, b) => s + Math.max(0, aging[b] ?? 0), 0);
  return (
    <div>
      {total > 0 && (
        <div aria-hidden="true" className="bg-surface-secondary mb-300 flex h-300 w-full overflow-hidden rounded-full">
          {BUCKETS.map((b) =>
            (aging[b] ?? 0) > 0 ? <div key={b} className={BAR_COLOR[b]} style={{ width: `${(Math.max(0, aging[b]) / total) * 100}%` }} /> : null,
          )}
        </div>
      )}
      <table className="w-full text-callout" aria-labelledby={id}>
        <tbody>
          {BUCKETS.map((b) => (
            <tr key={b} className="border-border-primary border-b last:border-b-0">
              <th scope="row" className="text-typography-secondary py-200 pr-300 text-left font-normal">
                <span aria-hidden="true" className={`${BAR_COLOR[b]} mr-200 inline-block size-200 rounded-full align-middle`} />
                {t(`aging.${b}` as MessageKey)}
              </th>
              <td className="text-typography-primary py-200 text-right font-medium tabular-nums">{formatAmount(aging[b] ?? 0, currency, lang)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
